import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ENTITY_TYPE_SELLER_OUTLET, STATUS_BEGIN, STATUS_FAIL } from "utils/constants/others";
import usePlanShipmentStore from "./usePlanShipmentStore";
import {
  formatInvoiceAmount,
  formatPurchaseOrderCount,
  formatScheduleDate,
  formatScheduleGroupNumber,
  formatShippingNoteNumber,
  formatSKUCount,
  formatSlotDisplayName,
  formatStatus,
  formatWeight,
  getMinDaysFromConfig,
  canSchedulePO,
  getSchedulingEnabledDate,
} from "./planShipmentUtils";
import {
  formatShippingNoteSlot,
  formatShippingNoteStatus,
  getCourierDocumentNumber,
  SHIPPING_NOTE_FILTERS,
} from "./planShipmentShippingNotesUtils";
import { PLAN_SHIPMENT_PATH } from "./PlanShipmentDrawerContext";
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

export const PRIMARY_TABS = {
  poSchedules: "po_schedules",
  shippingNotes: "shipping_notes",
};

export const PRIMARY_TAB_CONFIG = [
  { key: PRIMARY_TABS.poSchedules, label: "PO schedules" },
  { key: PRIMARY_TABS.shippingNotes, label: "Shipping notes" },
];

export const PO_STATUS_FILTER = { unscheduled: "unscheduled", scheduled: "scheduled" };
export const PO_STATUS_OPTIONS = [
  { value: PO_STATUS_FILTER.unscheduled, label: "Unscheduled" },
  { value: PO_STATUS_FILTER.scheduled, label: "Scheduled" },
];

export const SHIPPING_NOTE_STATUS_OPTIONS = SHIPPING_NOTE_FILTERS.filter((filter) => filter.id !== "all").map(
  (filter) => ({ value: filter.id, label: filter.label })
);

export const SHIPMENT_TYPE = { direct: "direct", courier: "courier" };
export const SHIPMENT_TYPE_OPTIONS = [
  { value: SHIPMENT_TYPE.direct, label: "Direct delivery" },
  { value: SHIPMENT_TYPE.courier, label: "Courier/part load" },
];

const EMPTY_VALUE = "—";

const getShipmentType = (shippingNote) =>
  String(shippingNote?.delivery_details?.delivery_type || "").toLowerCase() === "courier"
    ? SHIPMENT_TYPE.courier
    : SHIPMENT_TYPE.direct;

const getShipmentReference = (shippingNote) => {
  const deliveryDetails = shippingNote?.delivery_details || {};
  if (getShipmentType(shippingNote) === SHIPMENT_TYPE.courier) {
    return {
      primary: deliveryDetails.da_name || EMPTY_VALUE,
      secondary: getCourierDocumentNumber(shippingNote),
    };
  }
  return {
    primary: deliveryDetails.vehicle_number || EMPTY_VALUE,
    secondary: deliveryDetails.da_name || EMPTY_VALUE,
  };
};

const getWarehouseLabel = (raw) => raw || EMPTY_VALUE;

const getInitialTab = (state) =>
  state?.activeTab === PRIMARY_TABS.shippingNotes ? PRIMARY_TABS.shippingNotes : PRIMARY_TABS.poSchedules;

export default function usePlanShipmentListLogic() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getPOScheduleGroups,
    getPOScheduleGroupsStatus,
    poScheduleGroups,
    poScheduleGroupsError,
    poScheduleGroupsHasMore,
    searchPOScheduleGroups,
    getPurchaseOrders,
    getPurchaseOrdersStatus,
    purchaseOrders,
    purchaseOrdersError,
    purchaseOrdersHasMore,
    searchPurchaseOrders,
    getAdvanceShippingNotes,
    getAdvanceShippingNotesStatus,
    advanceShippingNotes,
    advanceShippingNotesError,
    advanceShippingNotesHasMore,
    searchAdvanceShippingNotes,
    clearShippingNoteSelection,
    getPlanShipmentHelpSections,
    planShipmentAsnConfig,
  } = usePlanShipmentStore();

  const vendorId = window.localStorage.getItem("outlet_id");
  const hasValidVendorId = Number.isInteger(Number(vendorId)) && Number(vendorId) > 0;
  const minDays = getMinDaysFromConfig(planShipmentAsnConfig);

  const [activeTab, setActiveTab] = useState(getInitialTab(location.state));
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQueryState] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  // Draft values are edited in the filter card; "Apply filters" copies them to applied.
  const [draftPoStatus, setDraftPoStatus] = useState(
    location.state?.activeScheduleTab === PO_STATUS_FILTER.scheduled
      ? PO_STATUS_FILTER.scheduled
      : PO_STATUS_FILTER.unscheduled
  );
  const [appliedPoStatus, setAppliedPoStatus] = useState(draftPoStatus);
  const [draftNoteStatus, setDraftNoteStatus] = useState(null);
  const [appliedNoteStatus, setAppliedNoteStatus] = useState(null);
  const [draftShipmentType, setDraftShipmentType] = useState(null);
  const [appliedShipmentType, setAppliedShipmentType] = useState(null);

  const isPoTab = activeTab === PRIMARY_TABS.poSchedules;
  const isScheduledView = isPoTab && appliedPoStatus === PO_STATUS_FILTER.scheduled;
  const trimmedSearchQuery = searchQuery.trim();

  const pathname = location.pathname.replace(/\/+$/, "");
  const isDrawerOpen = pathname !== PLAN_SHIPMENT_PATH;

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(() => {
    clearShippingNoteSelection();
    if (hasValidVendorId) {
      getPlanShipmentHelpSections({ seller_outlet_id: vendorId });
    }
  }, [clearShippingNoteSelection, getPlanShipmentHelpSections, hasValidVendorId, vendorId]);

  // The list stays mounted behind the drawer, so tab/filter requests from the
  // drawer screens (e.g. "show Scheduled after creating a schedule") arrive via
  // location.state and are consumed here once the drawer is closed.
  useEffect(() => {
    const state = location.state;
    if (isDrawerOpen || !state || (!state.activeTab && !state.activeScheduleTab)) return;

    if (state.activeTab === PRIMARY_TABS.shippingNotes) {
      setActiveTab(PRIMARY_TABS.shippingNotes);
    } else {
      setActiveTab(PRIMARY_TABS.poSchedules);
      if (state.activeScheduleTab) {
        setDraftPoStatus(state.activeScheduleTab);
        setAppliedPoStatus(state.activeScheduleTab);
      }
    }
    setPage(1);
    setSearchQueryState("");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.key, isDrawerOpen, location.state, location.pathname, navigate]);

  // Anything done in the drawer may have changed the list, so refetch on close.
  const wasDrawerOpen = useRef(isDrawerOpen);
  useEffect(() => {
    if (wasDrawerOpen.current && !isDrawerOpen) {
      clearShippingNoteSelection();
      refresh();
    }
    wasDrawerOpen.current = isDrawerOpen;
  }, [isDrawerOpen, clearShippingNoteSelection, refresh]);

  const noteStatuses = useMemo(
    () =>
      (SHIPPING_NOTE_FILTERS.find((filter) => filter.id === (appliedNoteStatus || "all")) || SHIPPING_NOTE_FILTERS[0])
        .statuses,
    [appliedNoteStatus]
  );

  useEffect(() => {
    if (!hasValidVendorId) return undefined;

    let request;
    if (isPoTab) {
      if (appliedPoStatus === PO_STATUS_FILTER.scheduled) {
        request = trimmedSearchQuery
          ? () => searchPOScheduleGroups({ vendor_id: vendorId, schedule_group_id: Number(trimmedSearchQuery) })
          : () => getPOScheduleGroups({ vendor_id: vendorId, page_size: PAGE_SIZE, page_number: page });
      } else {
        request = trimmedSearchQuery
          ? () => searchPurchaseOrders({ vendor_id: vendorId, purchase_order_id: Number(trimmedSearchQuery) })
          : () => getPurchaseOrders({ vendor_id: vendorId, page_size: PAGE_SIZE, page_number: page });
      }
    } else {
      request = trimmedSearchQuery
        ? () =>
            searchAdvanceShippingNotes({
              entity_type: ENTITY_TYPE_SELLER_OUTLET,
              entity_id: vendorId,
              asn_id: Number(trimmedSearchQuery),
            })
        : () =>
            getAdvanceShippingNotes({
              entity_type: ENTITY_TYPE_SELLER_OUTLET,
              entity_id: vendorId,
              page_size: PAGE_SIZE,
              page_number: page,
              statuses: noteStatuses,
            });
    }

    if (!trimmedSearchQuery) {
      request();
      return undefined;
    }
    const timeoutId = setTimeout(request, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [
    hasValidVendorId,
    vendorId,
    isPoTab,
    appliedPoStatus,
    noteStatuses,
    page,
    trimmedSearchQuery,
    refreshKey,
    getPOScheduleGroups,
    searchPOScheduleGroups,
    getPurchaseOrders,
    searchPurchaseOrders,
    getAdvanceShippingNotes,
    searchAdvanceShippingNotes,
  ]);

  const handleTabChange = useCallback((tabKey) => {
    setActiveTab(tabKey);
    setPage(1);
    setSearchQueryState("");
  }, []);

  const setSearchQuery = useCallback((value) => {
    setSearchQueryState(String(value || "").replace(/\D/g, ""));
    setPage(1);
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedPoStatus(draftPoStatus);
    setAppliedNoteStatus(draftNoteStatus);
    setAppliedShipmentType(draftShipmentType);
    setPage(1);
  }, [draftPoStatus, draftNoteStatus, draftShipmentType]);

  const clearFilters = useCallback(() => {
    setDraftPoStatus(PO_STATUS_FILTER.unscheduled);
    setAppliedPoStatus(PO_STATUS_FILTER.unscheduled);
    setDraftNoteStatus(null);
    setAppliedNoteStatus(null);
    setDraftShipmentType(null);
    setAppliedShipmentType(null);
    setPage(1);
  }, []);

  const hasPendingFilterChanges = isPoTab
    ? draftPoStatus !== appliedPoStatus
    : draftNoteStatus !== appliedNoteStatus || draftShipmentType !== appliedShipmentType;

  const openPurchaseOrder = useCallback(
    (po) => navigate(`${PLAN_SHIPMENT_PATH}/purchase-orders/${po.id}`, { state: { primaryPoData: po } }),
    [navigate]
  );
  const openScheduleGroup = useCallback(
    (scheduleGroup) => navigate(`${PLAN_SHIPMENT_PATH}/schedule-groups/${scheduleGroup.schedule_group_id}`),
    [navigate]
  );
  const openShippingNote = useCallback(
    (shippingNote) =>
      navigate(`${PLAN_SHIPMENT_PATH}/shipping-notes/${shippingNote.asn_id}`, {
        state: { asnNumber: formatShippingNoteNumber(shippingNote) },
      }),
    [navigate]
  );

  const purchaseOrderRows = useMemo(
    () =>
      purchaseOrders.map((po) => ({
        id: po.id,
        poNumber: po.purchase_order_number || EMPTY_VALUE,
        skus: formatSKUCount(po.total_skus),
        netWeight: formatWeight(po.total_weight),
        deliverBy: formatScheduleDate(po.expected_delivery_date),
        amount: formatInvoiceAmount(po.po_amount),
        deliveryAt: getWarehouseLabel(po.warehouse_code),
        status: formatStatus(po.derived_status),
        isSchedulable: canSchedulePO(po.expected_delivery_date, minDays),
        schedulingEnabledDate: getSchedulingEnabledDate(po.expected_delivery_date, minDays),
        _raw: po,
      })),
    [purchaseOrders, minDays]
  );

  const scheduleGroupRows = useMemo(
    () =>
      poScheduleGroups.map((group) => ({
        id: group.schedule_group_id,
        groupNumber: formatScheduleGroupNumber(group),
        poCount: formatPurchaseOrderCount(group.purchase_order_count),
        netWeight: formatWeight(group.total_weight_in_kg),
        deliverBy: formatScheduleDate(group.schedule_date),
        slot: formatSlotDisplayName(group.slot),
        deliveryAt: getWarehouseLabel(group.warehouse_name),
        status: formatStatus(group.status),
        _raw: group,
      })),
    [poScheduleGroups]
  );

  const shippingNoteRows = useMemo(
    () =>
      advanceShippingNotes
        .filter(
          (note) =>
            Boolean(trimmedSearchQuery) || !appliedShipmentType || getShipmentType(note) === appliedShipmentType
        )
        .map((note) => ({
          id: `${note.status}-${note.asn_id}`,
          noteNumber: formatShippingNoteNumber(note),
          deliveryAt: getWarehouseLabel(note?.warehouse?.warehouse_name || note?.warehouse?.warehouse_code),
          deliverBy: formatScheduleDate(note.delivery_date),
          slot: formatShippingNoteSlot(note.slot),
          invoices: Number(note.invoice_count) || 0,
          netWeight: formatWeight(note.total_weight_in_kg),
          shipmentType:
            getShipmentType(note) === SHIPMENT_TYPE.courier ? "Courier/part load" : "Direct delivery",
          reference: getShipmentReference(note),
          status: formatShippingNoteStatus(note.status),
          _raw: note,
        })),
    [advanceShippingNotes, appliedShipmentType, trimmedSearchQuery]
  );

  const current = isScheduledView
    ? {
        status: getPOScheduleGroupsStatus,
        error: poScheduleGroupsError,
        hasNextPage: poScheduleGroupsHasMore && !trimmedSearchQuery,
        rows: scheduleGroupRows,
      }
    : isPoTab
    ? {
        status: getPurchaseOrdersStatus,
        error: purchaseOrdersError,
        hasNextPage: purchaseOrdersHasMore && !trimmedSearchQuery,
        rows: purchaseOrderRows,
      }
    : {
        status: getAdvanceShippingNotesStatus,
        error: advanceShippingNotesError,
        hasNextPage: advanceShippingNotesHasMore && !trimmedSearchQuery,
        rows: shippingNoteRows,
      };

  return {
    // state
    activeTab,
    isPoTab,
    isScheduledView,
    page,
    searchQuery,
    isSearching: Boolean(trimmedSearchQuery),
    hasValidVendorId,
    isLoading: hasValidVendorId && (current.status === STATUS_BEGIN || !current.status),
    hasError: current.status === STATUS_FAIL,
    errorMessage: current.error,
    hasNextPage: current.hasNextPage,
    rows: current.rows,
    // filters
    draftPoStatus,
    setDraftPoStatus,
    draftNoteStatus,
    setDraftNoteStatus,
    draftShipmentType,
    setDraftShipmentType,
    hasPendingFilterChanges,
    // actions
    handleTabChange,
    setSearchQuery,
    goToPage: setPage,
    applyFilters,
    clearFilters,
    refresh,
    openPurchaseOrder,
    openScheduleGroup,
    openShippingNote,
  };
}
